import React from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { compose } from 'react-apollo';
import GraphQLClient from '@api/graphql/GraphQLClient';
import * as Mutations from '@api/graphql/mutations';
import * as Queries from '@api/graphql/queries';
import { coupon, couponVariables, coupon_coupon, coupon_coupon_codes_data, coupon_coupon_products_data, updateCouponVariables } from '@api/graphql/types';
import { ApplicationState } from 'reducers/';
import { connect } from 'react-redux';
import CouponForm from '../components/CouponForm';
import { ApolloQueryResult } from 'apollo-client';
import { removeProp } from '@utils/Object';
import { ProductIdName } from '@modules/PromotionCategory/components/ProductInput';
import Notification from 'components/Notification';
import { Loading } from '@components/Loading';
import { CodeCodeUsageCount } from "../components/CouponCodesField";

type Props = {
    id: string,
} & InjectedIntlProps & RouteComponentProps<{ id: string }>;


interface State {
    formData: updateCouponVariables | null;
    codes: CodeCodeUsageCount[];
    products: ProductIdName[];
    originalMaxUsageCount: number;
    generatedCodesCount: number | null;
}

class CouponEdit extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            formData: null,
            codes: [],
            products: [],
            originalMaxUsageCount: 1,
            generatedCodesCount: null,
        };
    }

    public componentDidMount() {
        GraphQLClient.query<coupon, couponVariables>({
            query: Queries.coupon,
            variables: { id: this.props.id },
        }).then((response: ApolloQueryResult<coupon>) => {
            if (response.data && response.data.coupon) {
                const couponResponse = response.data.coupon;
                this.setState({
                    formData: this.convertResponse(couponResponse),
                    codes: this.storeCodes(couponResponse),
                    products: this.storeProducts(couponResponse),
                    originalMaxUsageCount: couponResponse.usage_count,
                    generatedCodesCount: couponResponse.generated_codes_count,
                });
            }
        }).catch(() => {
            Notification.error(this.props.intl.formatMessage({ id: 'notification.failed' }));
        });
    }

    /**
     * Convert back-end response from coupon_coupon to updateCouponVariables and initialize coupon_code_attributes
     */
    private convertResponse = (formData: coupon_coupon): updateCouponVariables => {
        const newFormData: coupon_coupon = { ...formData };

        return {
            ...newFormData,
            shipping_method_id: (newFormData.shipping_method) ? newFormData.shipping_method.id : null,
            payment_method_id: (newFormData.payment_method) ? newFormData.payment_method.id : null,
            codes: (newFormData.codes && newFormData.codes.data) ? newFormData.codes.data.map((obj: coupon_coupon_codes_data) => obj.code) : null,
            products: (newFormData.products && newFormData.products.data) ? newFormData.products.data.map((obj: coupon_coupon_products_data) => obj.id) : null,
            coupon_code_attributes: {
                count: 0,
                length: 0,
                type: null,
                prefix: '',
                suffix: '',
            },
        };
    }

    /**
     * We store the codes so we can show the usage_count in CouponCodesField
     */
    private storeCodes = (couponResponse: coupon_coupon) => {
        let codes: CodeCodeUsageCount[] = [];
        if (couponResponse.codes && couponResponse.codes.data) {
            codes = couponResponse.codes.data.map((code: coupon_coupon_codes_data) => {
                return {
                    code: code.code,
                    usage_count: code.usage_count,
                };
            });
        }
        return codes;
    }

    /**
     * We store the products so we can show the name in ProductInput
     */
    private storeProducts = (couponResponse: coupon_coupon) => {
        let products: ProductIdName[] = [];
        if (couponResponse.products && couponResponse.products.data) {
            products = couponResponse.products.data.map((obj: coupon_coupon_products_data) => {
                return {
                    id: obj.id,
                    name: obj.name,
                };
            });
        }
        return products;
    }

    private cleanFormDataForSubmit = (formData: updateCouponVariables, codes: CodeCodeUsageCount[],  products: ProductIdName[]): updateCouponVariables => {
        const newFormData: updateCouponVariables = { ...formData };

        // Removing '__typename'
        removeProp(newFormData, '__typename');

        // We have to get the coupon codes from the CodeCodeUsageCount type
        newFormData.codes = codes.map((code: CodeCodeUsageCount) => code.code);

        // We have to get the product ids from the ProductIdName type
        newFormData.products = products.map((product: ProductIdName) => product.id);

        return newFormData;
    }

    public onSubmit = (formData: updateCouponVariables, codes: CodeCodeUsageCount[], products: ProductIdName[]) => {
        return GraphQLClient.mutate({
            mutation: Mutations.updateCoupon,
            variables: this.cleanFormDataForSubmit(formData, codes, products),
        });
    }

    public render() {
        if (!this.state.formData || typeof this.state.generatedCodesCount !== "number") {
            return <Loading />;
        }

        return (
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h5>{this.props.intl.formatMessage({ id: 'module.coupon.couponEdit' })}</h5>
                        </div>
                        <div className="ibox-content">
                            <CouponForm
                                formData={this.state.formData}
                                codes={this.state.codes}
                                products={this.state.products}
                                originalMaxUsageCount={this.state.originalMaxUsageCount}
                                generatedCodesCount={this.state.generatedCodesCount}
                                onSubmit={this.onSubmit}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: ApplicationState, props: Props) => {
    const { id } = props.match.params;
    return { id };
};

export default compose(
    injectIntl,
    withRouter,
    connect(mapStateToProps),
)(CouponEdit);



// WEBPACK FOOTER //
// ./src/modules/Coupon/pages/CouponEdit.tsx